import Decimal from 'decimal.js'

export type MilestoneAllocationRow = { percent: number; sequence: number | null; id?: string | null }

/**
 * The payment-milestone cent rule, shared with proposal-api
 * (`allocate_milestone_amounts`) and the activation plug-in
 * (`AllocateMilestoneAmounts`); parity vectors: milestone_allocation_vectors.json.
 *
 * The total is rounded HALF_UP to cents. When the percents total exactly 100,
 * shares use largest remainder: each row gets the floor of its exact share of
 * |total| in cents, then the leftover cents go one each to the rows with the
 * largest fractional parts, ties to the earlier row in schedule order
 * (sequence ascending, null as 0; then milestone id, lowercase ordinal, missing
 * as ''; then input order); the total's sign applies to every share, so they
 * sum exactly to the total. Otherwise each share is rounded HALF_UP on its own.
 * Amounts come back in input order.
 */
export function allocateMilestoneAmounts(total: number, rows: MilestoneAllocationRow[]): number[] {
  const totalCents = new Decimal(total).toDecimalPlaces(2).mul(100)
  const percents = rows.map((row) => new Decimal(row.percent))
  const toAmount = (cents: Decimal) => cents.div(100).toNumber() + 0 // + 0 turns -0 into 0
  if (!percents.reduce((sum, percent) => sum.add(percent), new Decimal(0)).equals(100)) {
    return percents.map((percent) => toAmount(totalCents.mul(percent).div(100).toDecimalPlaces(0)))
  }

  const absCents = totalCents.abs()
  const shares = percents.map((percent) => {
    const exact = absCents.mul(percent).div(100)
    const cents = exact.floor()
    return { cents, fraction: exact.sub(cents) }
  })
  const scheduleOrder = rows
    .map((row, index) => ({ index, sequence: row.sequence ?? 0, id: (row.id ?? '').toLowerCase() }))
    .sort((a, b) => a.sequence - b.sequence || compareOrdinal(a.id, b.id) || a.index - b.index)
    .map(({ index }) => index)
  const remainder = absCents.sub(shares.reduce((sum, share) => sum.add(share.cents), new Decimal(0))).toNumber()
  scheduleOrder
    .map((index, position) => ({ index, position }))
    .sort((a, b) => shares[b.index].fraction.cmp(shares[a.index].fraction) || a.position - b.position)
    .slice(0, remainder)
    .forEach(({ index }) => {
      shares[index].cents = shares[index].cents.add(1)
    })
  return shares.map(({ cents }) => toAmount(totalCents.isNegative() ? cents.neg() : cents))
}

const compareOrdinal = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

export function calculateMilestoneAmounts<
  T extends {
    dsy_name: string
    dsy_percent: number
    dsy_sequencenumber: number | null
    dsy_paymentmilestoneid?: string | null
  },
>(milestones: T[], total: number): (T & { dsy_amount: number })[] {
  const amounts = allocateMilestoneAmounts(
    total,
    milestones.map((milestone) => ({
      percent: milestone.dsy_percent,
      sequence: milestone.dsy_sequencenumber,
      id: milestone.dsy_paymentmilestoneid,
    }))
  )
  return milestones.map((milestone, index) => ({ ...milestone, dsy_amount: amounts[index] }))
}
